import { useMemo, useState, type ReactNode } from 'react';
import { IconSizes, SearchMdIcon } from '@newcore/icons';
import { FontWeights } from '@newcore/ui/enums/font-weights';
import { TextSizes } from '@newcore/ui/enums/typography-text-sizes';
import { Input } from '@newcore/ui/inputs';
import { Separator } from '@newcore/ui/separator';
import { Text } from '@newcore/ui/typography';
import { EmptyState } from '@/components/common/empty-state';
import { ErrorAlertWithRetry } from '@/components/common/error-alert-with-retry';
import { PageHeader } from '@/components/common/page-header';
import { FilterTabs, type FilterTabOption } from '@/components/ui/filter-tabs';
import { ApplicationTileSkeleton } from '@/components/ui/skeletons/application-tile-skeleton';
import { useRecentApps } from '@/hooks/use-recent-apps';
import { useApplications } from '@/hooks/use-user-portal';
import type { UserApplication } from '@/types/api.types';
import { ApplicationTile } from './application-tile';

type ApplicationFilter = 'all' | 'favorites';

const RECENT_HEADING = 'Recently used';
const APP_TILE_SKELETON_COUNT = 14;
const NO_FAVORITES_MESSAGE = 'No favorite applications';
const NO_APPLICATIONS_MESSAGE = 'No applications available';
const EMPTY_APPLICATIONS: UserApplication[] = [];

function emptyCatalogMessage(
  filter: ApplicationFilter,
  query: string,
  hasApplicationSearchQuery: boolean
): string {
  if (hasApplicationSearchQuery) {
    return `Nothing matches “${query.trim()}”.`;
  }

  if (filter === 'favorites') {
    return NO_FAVORITES_MESSAGE;
  }

  return NO_APPLICATIONS_MESSAGE;
}

interface AllAppsSectionProps {
  onApplicationClick: (application: UserApplication) => void;
  loadingAppId?: string | null;
}

export const AllAppsSection = ({
  onApplicationClick,
  loadingAppId,
}: AllAppsSectionProps) => {
  const {
    data: applications,
    isLoading,
    isError,
    error,
    refetch,
  } = useApplications();
  const { recentAppIds } = useRecentApps();

  const [filter, setFilter] = useState<ApplicationFilter>('all');
  const [query, setQuery] = useState('');

  const allApps = applications?.items ?? EMPTY_APPLICATIONS;
  const totalCount = applications?.total ?? allApps.length;
  const isTruncated = allApps.length < totalCount;

  const recentApps = useMemo(() => {
    const byId = new Map(allApps.map(app => [app.app_id, app]));

    return recentAppIds
      .map(id => byId.get(id))
      .filter((app): app is UserApplication => Boolean(app));
  }, [allApps, recentAppIds]);

  const matches = useMemo(() => {
    const term = query.trim().toLowerCase();

    return allApps.filter(app => {
      if (filter === 'favorites' && !app.is_favorite) {
        return false;
      }

      if (!term) {
        return true;
      }

      return app.app_name.toLowerCase().includes(term);
    });
  }, [allApps, filter, query]);

  const hasApplicationSearchQuery = query.trim().length > 0;

  const tabs: FilterTabOption<ApplicationFilter>[] = [
    { value: 'all', label: 'All', count: totalCount },
    {
      value: 'favorites',
      label: 'Favorites',
      count: allApps.filter(app => app.is_favorite).length,
    },
  ];

  const showRecent =
    filter === 'all' && !hasApplicationSearchQuery && recentApps.length > 0;

  return (
    <div className='flex w-full flex-col gap-12'>
      <PageHeader
        title='Applications'
        description='Everything you have access to. Apps open right here in the portal.'
      />

      <div className='flex w-full flex-wrap items-center gap-4 lg:gap-12'>
        <FilterTabs
          className='flex-1'
          options={tabs}
          value={filter}
          onChange={setFilter}
          areCountsLoading={isLoading}
          aria-label='Filter applications'
        />
        <Input
          type='search'
          value={query}
          onChange={event => setQuery(event.target.value)}
          placeholder='Search apps'
          aria-label='Search apps'
          className='w-full sm:w-59'
          leading={
            <SearchMdIcon size={IconSizes.SM} className='text-quaternary' />
          }
        />
      </div>

      {isError && error && (
        <ErrorAlertWithRetry
          message={`Failed to load applications: ${error.message}`}
          onRetry={() => refetch()}
        />
      )}

      {isTruncated && !hasApplicationSearchQuery && (
        <Text size={TextSizes.SM} className='text-quaternary'>
          Showing {allApps.length} of {totalCount} applications. Search may not
          find the rest.
        </Text>
      )}

      <CatalogBody
        isLoading={isLoading}
        isError={isError}
        filter={filter}
        query={query}
        hasApplicationSearchQuery={hasApplicationSearchQuery}
        matches={matches}
        recentApps={recentApps}
        recentAppIds={recentAppIds}
        showRecent={showRecent}
        onApplicationClick={onApplicationClick}
        loadingAppId={loadingAppId}
      />
    </div>
  );
};

interface CatalogBodyProps {
  isLoading: boolean;
  isError: boolean;
  filter: ApplicationFilter;
  query: string;
  hasApplicationSearchQuery: boolean;
  matches: UserApplication[];
  recentApps: UserApplication[];
  recentAppIds: string[];
  showRecent: boolean;
  onApplicationClick: (application: UserApplication) => void;
  loadingAppId?: string | null;
}

function CatalogBody({
  isLoading,
  isError,
  filter,
  query,
  hasApplicationSearchQuery,
  matches,
  recentApps,
  recentAppIds,
  showRecent,
  onApplicationClick,
  loadingAppId,
}: CatalogBodyProps) {
  if (isLoading) {
    return (
      <div className='flex w-full flex-col gap-6'>
        {recentAppIds.length > 0 ? (
          <section className='flex w-full flex-col gap-3'>
            <SectionHeading>{RECENT_HEADING}</SectionHeading>
            <AppTileSkeletonGrid />
          </section>
        ) : (
          <AppTileSkeletonGrid />
        )}
      </div>
    );
  }

  if (isError) {
    return null;
  }

  if (matches.length === 0) {
    return (
      <EmptyState
        message={emptyCatalogMessage(filter, query, hasApplicationSearchQuery)}
      />
    );
  }

  if (hasApplicationSearchQuery) {
    return (
      <section className='flex w-full flex-col gap-3'>
        <SectionHeading>
          {matches.length} {matches.length === 1 ? 'result' : 'results'}
        </SectionHeading>
        <TileGrid
          apps={matches}
          onLaunch={onApplicationClick}
          loadingAppId={loadingAppId}
        />
      </section>
    );
  }

  return (
    <div className='flex w-full flex-col gap-6'>
      {showRecent && (
        <>
          <section className='flex w-full flex-col gap-3'>
            <SectionHeading>{RECENT_HEADING}</SectionHeading>
            <TileGrid
              apps={recentApps}
              onLaunch={onApplicationClick}
              loadingAppId={loadingAppId}
            />
          </section>
          <Separator />
        </>
      )}

      <TileGrid
        apps={matches}
        onLaunch={onApplicationClick}
        loadingAppId={loadingAppId}
      />
    </div>
  );
}

function AppTileSkeletonGrid() {
  return (
    <div className='flex flex-wrap gap-x-12 gap-y-6'>
      {Array.from({ length: APP_TILE_SKELETON_COUNT }, (_, index) => (
        <ApplicationTileSkeleton key={index} />
      ))}
    </div>
  );
}

function SectionHeading({ children }: { children: ReactNode }) {
  return (
    <Text as='h2' weight={FontWeights.MEDIUM} className='text-quaternary'>
      {children}
    </Text>
  );
}

interface TileGridProps {
  apps: UserApplication[];
  onLaunch: (application: UserApplication) => void;
  loadingAppId?: string | null;
}

function TileGrid({ apps, onLaunch, loadingAppId }: TileGridProps) {
  return (
    <div className='flex flex-wrap gap-x-12 gap-y-6'>
      {apps.map(app => (
        <ApplicationTile
          key={app.app_id}
          application={app}
          onClick={onLaunch}
          isLoading={loadingAppId === app.app_id}
        />
      ))}
    </div>
  );
}
